import { jwtDecode } from 'jwt-decode';
import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

/**
 * Senior Level Flow — candidate-facing, token-authenticated endpoints (PR #748 §3).
 *
 * There is no bearer auth: the signed link `token` (from the page URL) is passed
 * as a `?token=` query param on every call. Mirrors ResumeService.getProfilePublic
 * — `api` unwraps to the response body, and the payload sits under `data`.
 */
const withToken = (endpoint, token) =>
  `${endpoint}?token=${encodeURIComponent(token)}`;

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

// Every SENIOR_FLOW path needs sec_mapping_id, even on a magic link (they
// mirror MY_PIPELINES one-for-one under /public/). Where a caller already
// has it — e.g. off the landing response — `knownSecMappingId` skips this;
// otherwise it's decoded from the token's JWT payload (`secmappingid`)
// client-side only to route the request, the same as landing itself has to.
// The backend still re-verifies the `?token=` query param itself (see
// InterviewInviteService for the same pattern).
const secMappingIdFromToken = (token) => {
  try {
    const { secmappingid } = jwtDecode(token) || {};
    return secmappingid ?? null;
  } catch {
    return null;
  }
};

export const SeniorFlowService = {
  // GET landing info for the current status. The flow's entry point, so
  // there's nothing to read a known sec_mapping_id off yet — always decoded
  // from the token.
  getLanding: async (token) => {
    const secMappingId = secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.SENIOR_FLOW.LANDING, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(withToken(endpoint, token));
    return unwrap(response);
  },

  // POST the candidate's yes/no to the outreach.
  acknowledge: async (token, accepted, knownSecMappingId) => {
    const secMappingId = knownSecMappingId ?? secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.SENIOR_FLOW.ACKNOWLEDGE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.post(withToken(endpoint, token), { accepted });
    return unwrap(response);
  },

  // PATCH a flow-scoped profile section (shared { key, data } contract).
  updateProfileSection: async (token, key, data, knownSecMappingId) => {
    const secMappingId = knownSecMappingId ?? secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.SENIOR_FLOW.PROFILE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.patch(withToken(endpoint, token), {
      key,
      data,
    });
    return unwrap(response);
  },

  // POST interview acceptance (no body).
  acceptInterview: async (token) => {
    const response = await api.post(
      withToken(API_ENDPOINTS.SENIOR_FLOW.ACCEPT_INTERVIEW, token)
    );
    return unwrap(response);
  },

  // POST interview decline (no body).
  declineInterview: async (token) => {
    const response = await api.post(
      withToken(API_ENDPOINTS.SENIOR_FLOW.DECLINE_INTERVIEW, token)
    );
    return unwrap(response);
  },

  // ── Protected mirrors (bearer auth, no token — scoped by sec_mapping_id).
  // Same payload shapes as the magic-link endpoints above; used when this
  // flow is opened from the authenticated "My Applications" list instead of
  // an email link.
  getLandingProtected: async (secMappingId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.LANDING, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(endpoint);
    return unwrap(response);
  },

  acknowledgeProtected: async (secMappingId, accepted) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.ACKNOWLEDGE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.post(endpoint, { accepted });
    return unwrap(response);
  },

  updateProfileSectionProtected: async (secMappingId, key, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.PROFILE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.patch(endpoint, { key, data });
    return unwrap(response);
  },

  // GET the skill-match requirement list (job-derived skills the candidate
  // needs to confirm owning) for a magic-link token. `knownSecMappingId` —
  // when the caller already has it from the landing response — skips the
  // JWT decode; otherwise it's pulled from the token itself.
  getSkillMatch: async (token, knownSecMappingId) => {
    const secMappingId = knownSecMappingId ?? secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.SENIOR_FLOW.SKILL_MATCH, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(withToken(endpoint, token));
    return unwrap(response);
  },

  // PATCH the skills the candidate confirmed having — body: { skills: [...] }.
  updateSkillMatch: async (token, payload, knownSecMappingId) => {
    const secMappingId = knownSecMappingId ?? secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.SENIOR_FLOW.SKILL_MATCH, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.patch(withToken(endpoint, token), payload);
    return unwrap(response);
  },

  getSkillMatchProtected: async (secMappingId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.SKILL_MATCH, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(endpoint);
    return unwrap(response);
  },

  // PATCH the skills the candidate confirmed having — body: { skills: [...] }.
  updateSkillMatchProtected: async (secMappingId, payload) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.SKILL_MATCH, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.patch(endpoint, payload);
    return unwrap(response);
  },
};

export default SeniorFlowService;
